// sections/StockReconciliation.jsx
// v6: expose physicalReturnQty (physCount value) on each filteredRow for submit

import { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { S, colors } from "../styles/ChallanSettlementStyles";
import {
    Badge,
    BadgeGroup,
    Flag,
    ProgressBar,
    SectionCard,
} from "../utils/index";
import { Icons } from "../icons/Icons";
import { STOCK_TABLE_COLUMNS } from "../Constants/ChallanSetllementConstants";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import LandingScreenHeader from "../../../components/Comman/LandingScreenHeader";
import { getProductsDetailByChallanHeaderAndProductId } from "../../../Slice/vanStockTransfer.slice";
import { showLoador, showNotification } from "../../Landing/Landing.slice";
import {
    allMatchedMsg,
    noProductsMatchSearchMsg,
    noSkusBadgeMsg,
    noStockTransferDetailsForChallanMsg,
    searchProductsByNamePlaceholderMsg,
    serverMsg,
    settlerInstructionBodyMsg,
    settlerInstructionLabelMsg,
    stockReconciliationFootnoteMsg,
    stockReconciliationTitleMsg,
    stockUnitMismatchSuffixMsg,
    viewProductDetailsMsg,
} from "../../../config/messageconstant";
import {
    challanBorderInput,
    challanDetailsViewBtnBorderColor,
    challanIndigoBg,
    challanSvgBillDarkFill,
    challanTextMuted3,
    white,
    whiteSlightBlue,
} from "../../../config/ColorObj";
import { compose } from "recompose";
import { withRouter } from "../../../components/withRouter";
import { connect } from "react-redux";
import PopUpProductDetails from "./PopUpProductDetails";

// ─── Inline styles ────────────────────────────────────────────────────────────

const LS = {
    scrollOuter: {
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${colors.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        maxHeight: "420px",
    },
    scrollInner: {
        overflowY: "auto",
        overflowX: "auto",
        flex: 1,
        scrollbarWidth: "thin",
        scrollbarColor: `${challanBorderInput} transparent`,
    },
    stickyThead: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: whiteSlightBlue,
    },
    noResults: {
        padding: "32px 16px",
        textAlign: "center",
        color: challanTextMuted3,
        fontSize: "13px",
    },
    detailsBtn: {
        width: 30,
        height: 30,
        backgroundColor: challanIndigoBg,
        color: challanSvgBillDarkFill,
        border: `1px solid ${challanDetailsViewBtnBorderColor}`,
        transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
        "&:hover": {
            backgroundColor: challanSvgBillDarkFill,
            color: white,
            borderColor: challanSvgBillDarkFill,
            transform: "scale(1.05)",
        },
    },
    detailsIcon: {
        fontSize: 20,
    },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseCount = (raw) => parseInt(raw, 10);
const getDiff = (raw, expected) => {
    const val = parseCount(raw);
    return isNaN(val) ? 0 : val - expected;
    
};

const getExpected = (item) =>
    (item.secondaryQty ?? 0) +
    (item.secondaryInQty ?? 0) -
    (item.secondaryOutQty ?? 0) -
    (item.secondaryBillQty ?? 0);

const getSellPct = (item) => {
    const sellDenominator =
        (Number(item.secondaryQty) || 0) +
        (Number(item.secondaryInQty) || 0) -
        (Number(item.secondaryOutQty) || 0);

    return Number(item.secondaryBillQty) > 0 && sellDenominator > 0
        ? Number(
              (
                  ((Number(item.secondaryBillQty) || 0) / sellDenominator) * 100
              ).toFixed(1)
          )
        : 0;
};

const computeAggregates = (counts, skuRows) => {
    let gapTotal = 0, damagedUnits = 0, estimatedDamageValue = 0;
    skuRows.forEach((row) => {
        const parsed = parseCount(counts[row.id]);
        if (isNaN(parsed)) return;
        const diff = parsed - row.expected;
        if (diff !== 0) gapTotal += Math.abs(diff);
        if (diff < 0) {
            damagedUnits += Math.abs(diff);
            estimatedDamageValue += Math.abs(diff) * (row.rate ?? 0);
        }
    });
    return { gapTotal, damagedUnits, estimatedDamageValue };
};

// ─── DiffCell ─────────────────────────────────────────────────────────────────

const DiffCell = ({ diff }) => {
    if (diff === null) return <TypographyCompo style={S.diffPending}>—</TypographyCompo>;
    if (diff === 0) return <TypographyCompo style={S.diffZero}>0</TypographyCompo>;
    return (
        <TypographyCompo style={diff < 0 ? S.diffNeg : S.diffPos}>
            {diff > 0 ? "+" : ""}{diff}
        </TypographyCompo>
    );
};
DiffCell.propTypes = { diff: PropTypes.number };
DiffCell.defaultProps = { diff: null };

// ─── DetailsViewButton ────────────────────────────────────────────────────────

const DetailsViewButton = ({ onClick, label }) => (
    <Tooltip title={viewProductDetailsMsg} placement="top" arrow>
        <IconButton
            size="small"
            onClick={onClick}
            aria-label={label}
            sx={LS.detailsBtn}
        >
            <VisibilityIcon sx={LS.detailsIcon} />
        </IconButton>
    </Tooltip>
);
DetailsViewButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    label: PropTypes.string,
};
DetailsViewButton.defaultProps = { label: viewProductDetailsMsg };

// ─── SkuRow ───────────────────────────────────────────────────────────────────

const SkuRow = ({
    row,
    physCount,
    diff,
    onCountChange,
    onClickDetails,
}) => {
    const sellStyle =
        row.sellPct >= 50 ? S.sellPctHigh :
            row.sellPct >= 30 ? S.sellPctMid :
                S.sellPctLow;

    return (
        <tr style={diff === null ? {} : diff < 0 ? S.rowErr : diff > 0 ? S.rowDiff : {}}>
            <td style={S.td}>
                <TypographyCompo style={S.skuSeq}>{row.seq}</TypographyCompo>
            </td>
            <td style={S.td}>
                <div style={S.skuName}>{row.name}</div>
                <div style={S.skuCode}>{row.uom}</div>
            </td>
            <td style={{ ...S.td, ...S.tdRight }}>{row.loaded}</td>
            <td style={{ ...S.td, ...S.tdRight }}>{row.inQty}</td>
            <td style={{ ...S.td, ...S.tdRight }}>{row.outQty}</td>
            <td style={{ ...S.td, ...S.tdRight }}>{row.billed}</td>
            <td style={{ ...S.td, ...S.tdRight }}>
                <TypographyCompo style={S.rateValue}>
                    {row.rate != null ? `₹${row.rate}` : "—"}
                </TypographyCompo>
            </td>
            <td style={{ ...S.td, ...S.tdRight }}>{row.expected}</td>

            <td style={{ ...S.td, ...S.tdCenter }}>
                <input
                    type="number"
                    style={S.cellInput}
                    className="cs-input"
                    value={physCount}
                    min={0}
                    placeholder="—"
                    aria-label={`Physical count for ${row.name}`}
                    onChange={(e) => onCountChange(row.id, e.target.value)}
                />
            </td>

            <td style={{ ...S.td, ...S.tdRight }}>
                <DiffCell diff={diff} />
            </td>
            <td style={{ ...S.td, ...S.tdRight }}>
                <div style={sellStyle}>{row.sellPct}%</div>
                <ProgressBar pct={row.sellPct} color={sellStyle.color} />
            </td>
            <td style={{ ...S.td, ...S.tdCenter }}>
                <DetailsViewButton
                    onClick={() => onClickDetails(row)}
                    label={`View details for ${row.name}`}
                />
            </td>
        </tr>
    );
};

SkuRow.propTypes = {
    row: PropTypes.shape({
        id: PropTypes.string.isRequired,
        seq: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        uom: PropTypes.string,
        loaded: PropTypes.number.isRequired,
        inQty: PropTypes.number.isRequired,
        outQty: PropTypes.number.isRequired,
        billed: PropTypes.number.isRequired,
        rate: PropTypes.number,
        expected: PropTypes.number.isRequired,
        sellPct: PropTypes.number.isRequired,
    }).isRequired,
    physCount: PropTypes.string.isRequired,
    diff: PropTypes.number,
    onCountChange: PropTypes.func.isRequired,
    onClickDetails: PropTypes.func.isRequired,
};
SkuRow.defaultProps = { diff: null };

// ─── StockReconciliation ──────────────────────────────────────────────────────

const StockReconciliation = ({
    vanStockTransferDetail,
    onGapChange,
    onDamageChange,
    onPhysicalCountEntered,
    onRowsChange,
    challanHeaderId,
    getProductsDetailByChallanHeaderAndProductId,
    showNotification,
    showLoador,
}) => {

    const [searchQuery, setSearchQuery] = useState("");
    const [productDetailPopupOpen, setProductDetailPopupOpen] = useState(false);
    const [productDetailRows, setProductDetailRows] = useState([]);
    const [selectedProductName, setSelectedProductName] = useState("");

    // ── 1. Transform API items → stable skuRows ───────────────────────────────
    const skuRows = useMemo(
        () =>
            (vanStockTransferDetail ?? []).map((item, index) => ({
                id: item.id,
                seq: index + 1,
                productId: item.productId,
                name: item.productName ?? "—",
                uom: item.outletUomName ? `UOM: ${item.outletUomName}` : "",
                loaded: item.secondaryQty ?? 0,
                inQty: item.secondaryInQty ?? 0,
                outQty: item.secondaryOutQty ?? 0,
                billed: item.secondaryBillQty ?? 0,
                rate: item.outletRate ?? null,
                expected: getExpected(item),
                sellPct: getSellPct(item),
            })),
        [vanStockTransferDetail],
    );

    // ── 2. physCounts — single source of truth, keyed by item.id ─────────────
    const [physCounts, setPhysCounts] = useState(
        () => Object.fromEntries(skuRows.map((r) => [r.id, "0"]))
    );

    // Re-sync when API data arrives / changes — preserve already-typed values
    useEffect(() => {
        setPhysCounts((prev) => {
            const next = Object.fromEntries(skuRows.map((r) => [r.id, "0"])); skuRows.forEach((r) => {
                if (r.id in prev) next[r.id] = prev[r.id];
            });
            return next;
        });
    }, [skuRows]);

    // ── 3. enrichedRows — skuRows + physicalReturnQty stamped from physCounts ──
    //
    //    physicalReturnQty = the number the user typed in "Physical return" column.
    //    null  → user has not entered anything yet (input is blank)
    //    0–N   → parsed integer value ready to send to the API on submit
    //
    //    This is the array to pass to your submit handler.
    //    It is always the FULL list (all SKUs), never filtered —
    //    so submit captures every row even if some are hidden by search.
    const enrichedRows = useMemo(
        () =>
            skuRows.map((row) => {
                const raw = physCounts[row.id] ?? 0;
                const parsed = parseCount(raw);

                const physicalReturnQty = isNaN(parsed) ? 0 : parsed;

                const secondaryDiffQty =
                    physicalReturnQty - row.expected;

                return {
                    ...row,
                    physicalReturnQty,
                    secondaryDiffQty,
                };
            }),
        [skuRows, physCounts],
    );

    // Notify parent whenever enrichedRows change (optional callback)
    useEffect(() => {
        if (typeof onRowsChange === "function") {
            onRowsChange(enrichedRows);
        }
    }, [enrichedRows, onRowsChange]);

    // ── 4. filteredRows — search over enrichedRows so physicalReturnQty ────────
    //    is already present on every row the table renders
    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return enrichedRows;
        return enrichedRows.filter((row) =>
            row.name.toLowerCase().includes(q)
        );
    }, [enrichedRows, searchQuery]);

    // ── 5. Mismatch badge — always across ALL rows ────────────────────────────
    const totalMismatch = useMemo(
        () =>
            skuRows.reduce((sum, row) => {
                const d = getDiff(physCounts[row.id] ?? 0, row.expected);
                return d !== null && d !== 0 ? sum + Math.abs(d) : sum;
            }, 0),
        [physCounts, skuRows],
    );

    // ── 6. handleCountChange — functional updater, never stale ───────────────
    const handleCountChange = useCallback(
        (id, raw) => {
            setPhysCounts((prev) => {
                const updated = { ...prev, [id]: raw };
                const { gapTotal, damagedUnits, estimatedDamageValue } =
                    computeAggregates(updated, skuRows);
                onGapChange(gapTotal);
                onDamageChange({ damagedUnits, estimatedDamageValue });
                onPhysicalCountEntered(Object.values(updated).some((v) => v !== ""));
                return updated;
            });
        },
        [skuRows, onGapChange, onDamageChange, onPhysicalCountEntered],
    );

    const onSearch = useCallback((value) => {
        setSearchQuery(value ?? "");
    }, []);

    const handleCloseProductDetailPopup = useCallback(() => {
        setProductDetailPopupOpen(false);
    }, []);

    const handleProductDetailsClick = useCallback(
        async (row) => {
            try {
                showLoador({ loador: true });

                const { response, success } =
                    await getProductsDetailByChallanHeaderAndProductId({
                        headerId: challanHeaderId,
                        productId: row.productId,
                    });

                if (success) {
                    setProductDetailRows(response);
                    setSelectedProductName(row.name);
                    setProductDetailPopupOpen(true);
                } else {
                    showNotification({ msg: serverMsg, severity: "error" });
                }
            } catch (error) {
                console.error("Error fetching product details:", error);
            } finally {
                showLoador({ loador: false });
            }
        },
        [
            challanHeaderId,
            getProductsDetailByChallanHeaderAndProductId,
            showLoador,
            showNotification,
        ],
    );

    // ── Empty data guard ──────────────────────────────────────────────────────
    if (!vanStockTransferDetail || vanStockTransferDetail.length === 0) {
        return (
            <SectionCard
                themeKey="stock"
                icon={Icons.stock}
                title={stockReconciliationTitleMsg}
                badge={<Badge variant="gray" onDark>{noSkusBadgeMsg}</Badge>}
            >
                <TypographyCompo style={{ padding: "24px", color: colors.textMuted }}>
                    {noStockTransferDetailsForChallanMsg}
                </TypographyCompo>
            </SectionCard>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <SectionCard
            themeKey="stock"
            icon={Icons.stock}
            title={stockReconciliationTitleMsg}
            badge={
                <BadgeGroup>
                    <Badge variant={totalMismatch > 0 ? "red" : "green"} onDark>
                        {totalMismatch > 0
                            ? `${totalMismatch} ${stockUnitMismatchSuffixMsg}`
                            : allMatchedMsg}
                    </Badge>
                    <Badge variant="gray" onDark>
                        {skuRows.length} SKU{skuRows.length !== 1 ? "s" : ""}
                    </Badge>
                </BadgeGroup>
            }
        >
            <div style={S.flagWrapper}>
                <Flag variant="warn" dotColor={colors.amberMid}>
                    <TypographyCompo variant="body2">
                        <TypographyCompo as="span" weight={700}>
                            {settlerInstructionLabelMsg}
                        </TypographyCompo>{" "}
                        {settlerInstructionBodyMsg}
                    </TypographyCompo>
                </Flag>
            </div>

            <div style={S.tableSection}>
                <div style={LS.scrollOuter}>

                    <LandingScreenHeader
                        screenTitle=""
                        showSearchBox={true}
                        showTable={true}
                        showFilter={false}
                        showPdfDownload={false}
                        showExcelDownload={false}
                        embeddedSearch={true}
                        searchVariant="light"
                        searchPlaceholder={searchProductsByNamePlaceholderMsg}
                        onSearch={onSearch}
                    />

                    <div style={LS.scrollInner}>
                        <table style={{ ...S.table, borderRadius: 0 }}>
                            <thead style={{ ...S.thead, ...LS.stickyThead }}>
                                <tr>
                                    {STOCK_TABLE_COLUMNS.map(({ label, width, align, sub }) => (
                                        <th
                                            key={label}
                                            style={{ ...S.th, width, textAlign: align || "left" }}
                                        >
                                            {label}
                                            {sub && (
                                                <>
                                                    <br />
                                                    <TypographyCompo style={S.thSub}>{sub}</TypographyCompo>
                                                </>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {filteredRows.length > 0 ? (
                                    filteredRows.map((row) => (
                                        <SkuRow
                                            key={row.id}
                                            row={row}
                                            physCount={
                                                row.physicalReturnQty != null
                                                    ? String(row.physicalReturnQty)
                                                    : "0"
                                            }
                                            diff={getDiff(physCounts[row.id] ?? 0, row.expected)}
                                            onCountChange={handleCountChange}
                                            onClickDetails={handleProductDetailsClick}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={STOCK_TABLE_COLUMNS.length} style={LS.noResults}>
                                            {noProductsMatchSearchMsg} &ldquo;{searchQuery}&rdquo;
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </div>

                <TypographyCompo style={S.tableFootnote}>
                    {stockReconciliationFootnoteMsg}
                </TypographyCompo>
            </div>

            {productDetailPopupOpen && (
                <PopUpProductDetails
                    open={productDetailPopupOpen}
                    handleClose={handleCloseProductDetailPopup}
                    rows={productDetailRows}
                    productName={selectedProductName}
                />
            )}
        </SectionCard>
    );
};

StockReconciliation.propTypes = {
    vanStockTransferDetail: PropTypes.array.isRequired,
    onGapChange: PropTypes.func.isRequired,
    onDamageChange: PropTypes.func.isRequired,
    onPhysicalCountEntered: PropTypes.func.isRequired,
    onRowsChange: PropTypes.func,             // optional — receive enriched rows
};

StockReconciliation.defaultProps = {
    vanStockTransferDetail: [],
    onRowsChange: null,
};

const mapStateToProps = (state) => ({
});
const mapDispatchToProps = {
    showLoador,
    showNotification,
    getProductsDetailByChallanHeaderAndProductId,
};

export default compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
)(StockReconciliation);